import { Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { PlannerMessage, PlannerTaskTab, TaskStatusFilter } from '../types';
import { usePlannerMessagesStore } from '../store';
import useGetTabCounts from '../hooks/useGetTabCounts';
import { CountBadge } from 'components/shared/Badge/CountBadge';
import BulkActionBar from './BulkActionBar';
import {
  HistoryToggle,
  StyledTab,
  StyledTabs,
  TabsContainer,
  TabsRightSection
} from './TaskTypeTabs.styles';

interface TaskTypeTabsProps {
  messages: PlannerMessage[];
}

const TABS = [
  { value: PlannerTaskTab.ALL, labelKey: 'plannerMessages.tabs.all' },
  {
    value: PlannerTaskTab.LEAVE_REQUESTS,
    labelKey: 'plannerMessages.tabs.leaveRequests'
  },
  {
    value: PlannerTaskTab.DUTY_CHANGES,
    labelKey: 'plannerMessages.tabs.dutyChanges'
  },
  {
    value: PlannerTaskTab.FLEX_NOTIFICATIONS,
    labelKey: 'plannerMessages.tabs.flexNotifications'
  }
];

const TaskTypeTabs = ({ messages }: TaskTypeTabsProps) => {
  const { t } = useTranslation('common');
  const tab = usePlannerMessagesStore((state) => state.tab);
  const setTab = usePlannerMessagesStore((state) => state.setTab);
  const status = usePlannerMessagesStore((state) => state.status);
  const setStatus = usePlannerMessagesStore((state) => state.setStatus);
  const { data: tabCounts } = useGetTabCounts();

  return (
    <TabsContainer>
      <StyledTabs
        value={tab}
        onChange={(_, newValue: PlannerTaskTab) => setTab(newValue)}
      >
        {TABS.map(({ value, labelKey }) => {
          const count = tabCounts?.[value] ?? 0;
          const leaveSeenCount =
            value === PlannerTaskTab.LEAVE_REQUESTS
              ? tabCounts?.leaveSeenCount ?? 0
              : 0;

          let labelContent: React.ReactNode = t(labelKey);

          const badges = [
            ...(leaveSeenCount > 0
              ? [{ count: leaveSeenCount, color: 'warning' as const }]
              : []),
            ...(count > 0 ? [{ count, color: 'primary' as const }] : [])
          ];

          if (badges.length > 0) {
            labelContent = (
              <CountBadge badges={badges}>{t(labelKey)}</CountBadge>
            );
          }

          return <StyledTab key={value} value={value} label={labelContent} />;
        })}
      </StyledTabs>

      <TabsRightSection>
        <BulkActionBar messages={messages} />

        <HistoryToggle
          labelPlacement="start"
          control={
            <Switch
              checked={status === TaskStatusFilter.ALL}
              onChange={(_e, checked) =>
                setStatus(
                  checked ? TaskStatusFilter.ALL : TaskStatusFilter.OPEN
                )
              }
            />
          }
          label={t('plannerMessages.status.showHistory')}
          slotProps={{ typography: { variant: 'body2', noWrap: true } }}
        />
      </TabsRightSection>
    </TabsContainer>
  );
};

export default TaskTypeTabs;
